import { STREAM_PRIVACY_MASK } from '../config/streamPrivacy';
import { buildFeatherMask, privacyCornerRadius } from './drawStreamPrivacyMask';

const LIVE_MASK_MIN_INTERVAL_MS = 80;
const BLUR_PX = 22;

let lastPaintAt = 0;

function intersectRects(a, b) {
    const left = Math.max(a.left, b.left);
    const top = Math.max(a.top, b.top);
    const right = Math.min(a.right, b.right);
    const bottom = Math.min(a.bottom, b.bottom);
    if (right <= left || bottom <= top) return null;
    return { left, top, right, bottom, width: right - left, height: bottom - top };
}

function viewportToVideoCrop(viewRect, videoRect, vw, vh) {
    const ew = videoRect.width;
    const eh = videoRect.height;
    const scale = Math.min(ew / vw, eh / vh);
    const cw = vw * scale;
    const ch = vh * scale;
    const ox = (ew - cw) / 2;
    const oy = (eh - ch) / 2;

    const cx0 = viewRect.left - videoRect.left - ox;
    const cy0 = viewRect.top - videoRect.top - oy;
    const cx1 = viewRect.right - videoRect.left - ox;
    const cy1 = viewRect.bottom - videoRect.top - oy;

    const clipX0 = Math.max(0, cx0);
    const clipY0 = Math.max(0, cy0);
    const clipX1 = Math.min(cw, cx1);
    const clipY1 = Math.min(ch, cy1);

    if (clipX1 <= clipX0 || clipY1 <= clipY0) return null;

    const inv = 1 / scale;
    return {
        sx: Math.max(0, Math.floor(clipX0 * inv)),
        sy: Math.max(0, Math.floor(clipY0 * inv)),
        sw: Math.min(vw, Math.ceil((clipX1 - clipX0) * inv)),
        sh: Math.min(vh, Math.ceil((clipY1 - clipY0) * inv)),
    };
}

/**
 * iOS: backdrop-filter nad <video> nie działa — rysujemy rozmycie w tym samym
 * prostokącie co .stream-privacy-blur (z miękkimi krawędziami jak w CSS).
 */
export function paintLivePrivacyMaskOverlay(canvas, videoEl, { force = false } = {}) {
    if (!STREAM_PRIVACY_MASK.enabled || !canvas || !videoEl) return false;

    const now = performance.now();
    if (!force && now - lastPaintAt < LIVE_MASK_MIN_INTERVAL_MS) return false;

    const vw = videoEl.videoWidth;
    const vh = videoEl.videoHeight;
    if (!vw || !vh || videoEl.readyState < 2) return false;

    const stageEl = videoEl.closest('.player-stage');
    const maskEl = stageEl?.querySelector('.stream-privacy-blur');
    if (!stageEl || !maskEl) return false;

    const stageRect = stageEl.getBoundingClientRect();
    const videoRect = videoEl.getBoundingClientRect();
    const maskRect = maskEl.getBoundingClientRect();

    const onStage = intersectRects(maskRect, stageRect);
    const onVideo = onStage ? intersectRects(onStage, videoRect) : null;
    if (!onVideo || onVideo.width < 2 || onVideo.height < 2) return false;

    const crop = viewportToVideoCrop(onVideo, videoRect, vw, vh);
    if (!crop || crop.sw < 1 || crop.sh < 1) return false;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const dw = Math.round(stageRect.width);
    const dh = Math.round(stageRect.height);
    if (dw <= 0 || dh <= 0) return false;

    const cw = Math.round(dw * dpr);
    const ch = Math.round(dh * dpr);
    if (canvas.width !== cw || canvas.height !== ch) {
        canvas.width = cw;
        canvas.height = ch;
        canvas.style.width = `${dw}px`;
        canvas.style.height = `${dh}px`;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return false;

    const dispW = Math.round(onVideo.width * dpr);
    const dispH = Math.round(onVideo.height * dpr);
    const dispX = Math.round((onVideo.left - stageRect.left) * dpr);
    const dispY = Math.round((onVideo.top - stageRect.top) * dpr);

    const bleed = Math.round(Math.min(dispW, dispH) * 0.04);
    const blurPx = Math.round(BLUR_PX * dpr);
    const cornerR = privacyCornerRadius(dispW);

    const scratch = document.createElement('canvas');
    scratch.width = dispW + bleed * 2;
    scratch.height = dispH + bleed * 2;
    const sctx = scratch.getContext('2d');
    if (!sctx) return false;

    sctx.filter = `blur(${blurPx}px)`;
    sctx.drawImage(
        videoEl,
        crop.sx,
        crop.sy,
        crop.sw,
        crop.sh,
        bleed,
        bleed,
        dispW,
        dispH
    );
    sctx.filter = 'none';
    sctx.fillStyle = 'rgba(12, 18, 28, 0.28)';
    sctx.fillRect(0, 0, scratch.width, scratch.height);

    const patch = document.createElement('canvas');
    patch.width = dispW;
    patch.height = dispH;
    const pctx = patch.getContext('2d');
    if (!pctx) return false;

    pctx.drawImage(scratch, bleed, bleed, dispW, dispH, 0, 0, dispW, dispH);

    const feather = buildFeatherMask(dispW, dispH, cornerR);
    pctx.globalCompositeOperation = 'destination-in';
    pctx.drawImage(feather, 0, 0);

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, cw, ch);
    ctx.drawImage(patch, dispX, dispY);

    lastPaintAt = now;
    return true;
}
